import React from 'react'
import { Container, Row, Col } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import { i18nTranslate, parseMenuCategory, getLocaleCodeFromUrl } from 'src/utils'
import { observer } from 'mobx-react'
// import { LazyLoadImage } from 'react-lazy-load-image-component'
import { pageNames } from 'src/routes/pathParams'
import {
  imagePlaceholder,
  categoryImageUrl,
} from 'src/static/fixtures/imagePlaceholder'
import { APPConfig, categoryLandingTemplateId } from 'config/appConfig'
import { catalogContainer } from 'src/models'
import { NotFound } from '../NotFound'
import { BreadCrumb, MetaDecorator, ImageComponent } from 'src/views/components'
import { alertMessageKeys } from 'src/views/components/CartBlock/fixture'
import './styles.scss'

const templateClassNames = {
  template1: {
    background: 'pb-2 clp-banner-img',
    categoryBlock: 'px-1 pb-3 d-flex flex-column category-container',
    backgroundContainerWrapper: 'pb-5',
    subCategoryContainer:
      'mx-n1 mt-3 flex-column flex-lg-row flex-md-row flex-sm-row',
    imageWrap: 'product-image',
  },
  template2: {
    background: 'pb-2 mw-100 px-0',
    categoryBlock:
      'd-flex p-4 flex-column category-container subcategory-image text-center',
    backgroundContainerWrapper: 'pb-5 mw-100 px-0',
    subCategoryContainer:
      'mx-n1 mt-3 flex-column flex-lg-row flex-md-row flex-sm-row grid-container',
    imageWrap: '',
  },
}

const currentTemplateClass =
  templateClassNames[`template${categoryLandingTemplateId}`]

@observer
class CategoryLanding extends React.Component {
  componentDidMount() {
    this.ensureCategoryData()
  }

  componentDidUpdate(prevProps) {
    const categoryId = this.props.match?.params?.categoryId
    const prevCategoryId = prevProps.match?.params?.categoryId

    if (categoryId !== prevCategoryId) {
      this.ensureCategoryData()
    }
  }

  // getCategory loads the full top-category tree (topCategory API), not a single
  // category by ID. This page selects match.params.categoryId from subCategories.
  ensureCategoryData = async () => {
    const currentLocale = getLocaleCodeFromUrl() || ''

    if (
      catalogContainer.categoryResponse?.category &&
      catalogContainer.categoryResponseLocale === currentLocale
    ) {
      return
    }

    try {
      await catalogContainer.getCategory({
        contextName: 'categoryData',
      })
    } catch (error) {
      console.error('[CategoryLanding] failed to load category data:', error)
    }
  }

  renderBackgroundContainer = (categoryDetails = {}) => {
    const videoURL = categoryDetails?.properties?.videoURL || ''
    const backgroundImage =
      categoryDetails?.properties?.imageURL || categoryImageUrl
    const name = categoryDetails?.properties?.name || 'Image'

    const backgroundView =
      videoURL !== '' ? (
        <video className="mw-100" autoPlay>
          <source src={videoURL} />
        </video>
      ) : (
        <img
          loading="lazy"
          className="w-100 justify-content-center"
          src={backgroundImage || imagePlaceholder}
          alt={name}
          onError={e => {
            if (e.target.src != imagePlaceholder) {
              e.target.onerror = null
              e.target.src = imagePlaceholder
            }
          }}
        />
      )

    return (
      <Container className={currentTemplateClass.background}>
        {backgroundView}
      </Container>
    )
  }

  renderSubCategoryList = (categoryDetails = {}) => {
    const identifier = categoryDetails?.identifier || ''
    const name = parseMenuCategory(categoryDetails?.properties?.name || '')
    const templateId = categoryDetails?.milestone?.templateId || ''

    const url = templateId
      ? pageNames[`template${templateId}`]
      : pageNames.catalog
    const link = `${url}/${identifier}`
    return (
      <Row noGutters key={identifier}>
        <Link className="category-title" to={link} title={name}>
          <Col className="pl-0">{name}</Col>
        </Link>
      </Row>
    )
  }

  renderCategoryListBlock = (categoryDetails = {}) => {
    const identifier = categoryDetails?.identifier || ''
    const image = categoryDetails?.properties?.imageURL || ''
    let name = parseMenuCategory(categoryDetails?.properties?.name || '')
    const subCategories = categoryDetails?.subCategories || []
    const templateId = categoryDetails?.milestone?.templateId || ''

    const url = templateId
      ? pageNames[`template${templateId}`]
      : pageNames.catalog
    const link = `${url}/${identifier}`

    // related to CX121-4435
    // pdn fix requested by Kas
    // discussed with Anto/Poovarasan
    const parentCategoryRedirectionMapping =
      APPConfig?.getAppConfig()?.parentCategoryRedirectionMapping || {}
    const shouldRedirectToParentCategoryFromStoreConfig =
      APPConfig?.getAppConfig()?.shouldRedirectToParentCategory === 'true'
    const isToRedirectToCategoryLanding =
      shouldRedirectToParentCategoryFromStoreConfig &&
      Object.keys(parentCategoryRedirectionMapping).some(category => {
        return identifier == category
      })
    let viewAllRedirectLink = `${url}/${identifier}`
    if (isToRedirectToCategoryLanding) {
      viewAllRedirectLink = `${pageNames.categoryLanding}/${parentCategoryRedirectionMapping[identifier]}`
    }
    const viewAllTxt = i18nTranslate('menu.viewAll', 'View All', {
      nameSpace: 'ssr-resource',
    })

    // if (!name && categoryLandingTemplateId == 1) {
    //   name = categoryDetails?.milestone?.name
    // }

    return (
      <Col className={currentTemplateClass.categoryBlock} key={identifier}>
        <div className={currentTemplateClass.imageWrap}>
          <ImageComponent
            testid="qa-product-image"
            src={image}
            alt={
              name +
              i18nTranslate('clp.image', ' Image', {
                nameSpace: 'ssr-resource',
              })
            }
            height={'200px'}
            width={'200px'}
          />
          {categoryLandingTemplateId === 2 && (
            <Link className="category-title text-center pt-3 d-block" to={link}>
              <h4>{name}</h4>
            </Link>
          )}
        </div>
        {categoryLandingTemplateId === 1 && (
          <Container className="pt-3 clp-header">
            <h2 className="font-weight-bold clp-header-title">{name}</h2>
            <Row noGutters>
              <Link
                className="category-title"
                to={isToRedirectToCategoryLanding ? viewAllRedirectLink : link}
                aria-label={`${viewAllTxt} ${name}`}>
                <Col className="pl-0">{viewAllTxt}</Col>
              </Link>
            </Row>
            {subCategories.map(this.renderSubCategoryList)}
          </Container>
        )}
      </Col>
    )
  }

  render() {
    const categoryId = this.props.match?.params?.categoryId || ''
    const categoryList =
      catalogContainer.categoryResponse?.category?.subCategories || []

    const categoryDetails = categoryList.find(
      category => category.identifier === categoryId
    )
    if (!categoryId) {
      return <NotFound />
    }
    const subCategories = categoryDetails?.subCategories || []
    const categoryLandingTitleName = categoryDetails?.properties?.name || ''
    const categoryLandingTitle =
      alertMessageKeys(categoryLandingTitleName)
        ?.replace?.(' | Nu Skin', '')
        ?.replace?.('Nu Skin | ', '') + ' | Nu Skin'
    const catDesc = categoryDetails?.properties?.description || []
    const breadCrumbList = [
      {
        label: i18nTranslate('breadCrumb.home', 'Home', {
          nameSpace: 'ssr-resource',
        }),
        value: '/',
      },
      {
        label: categoryDetails?.properties?.name || '',
        value: '',
        isLastElement: true,
      },
    ]
    return (
      <>
        <MetaDecorator
          title={parseMenuCategory(categoryLandingTitle)}
          imageUrl=""
          imageAlt=""
          description={catDesc}
          location={this.props}
        />
        <BreadCrumb breadCrumbList={breadCrumbList} />
        <Container className={currentTemplateClass.backgroundContainerWrapper}>
          {this.renderBackgroundContainer(categoryDetails)}
          <Container className="mw-100 px-0">
            <Row
              noGutters
              className={`sub-categories-render ${currentTemplateClass.subCategoryContainer}`}>
              {subCategories.map(this.renderCategoryListBlock)}
            </Row>
          </Container>
        </Container>
      </>
    )
  }
}

export { CategoryLanding }

// export default CategoryLanding
export default CategoryLanding
